import styled from 'styled-components';
import { IconFile, IconTrash } from './icons';

/**
 * Компактный список выбранных File с возможностью удалить
 * отдельный элемент. Используется в multi-file загрузке (комплект
 * договора, документы по делу). Если массив пуст — компонент
 * ничего не рендерит, удобно как «хвост» под FileDropZone.
 */

const List = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FileIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Size = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  flex-shrink: 0;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    background: ${({ theme }) => theme.colors.status.danger}14;
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.status.danger};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let idx = 0;
  let val = bytes;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx += 1;
  }
  // 2 знака после запятой для МБ/ГБ, целое — для Б/КБ.
  const formatted = idx >= 2 ? val.toFixed(2) : Math.round(val).toString();
  return `${formatted.replace('.', ',')} ${units[idx]}`;
}

interface Props {
  files: File[];
  onRemove: (idx: number) => void;
}

export function MultiFileList({ files, onRemove }: Props) {
  if (files.length === 0) return null;
  return (
    <List>
      {files.map((f, idx) => (
        <Item key={`${f.name}-${idx}-${f.size}`}>
          <FileIcon>
            <IconFile size={16} />
          </FileIcon>
          <Name title={f.name}>{f.name}</Name>
          <Size>{humanSize(f.size)}</Size>
          <RemoveBtn
            type="button"
            onClick={() => onRemove(idx)}
            aria-label={`Удалить ${f.name}`}
            title="Удалить"
          >
            <IconTrash size={14} />
          </RemoveBtn>
        </Item>
      ))}
    </List>
  );
}
